<!-- Ported from the Polaris kit: src/product/components/charts/ChartCard.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase 7.1, 29/09):
     - strings through i18n ("About {label}" is the StatTile's key; the rest is POLARIS_REPORTS.CHART_CARD);
     - `emptyTitle`/`emptyDescription` props: each report says "no data" in its own words (7-7: compact Empty inside
       the card, the i18n sentence the woot already has);
     - PROVISÓRIO (R2-X-4 = B, 7-9): an `actions` slot at the end of the header, for what the woot puts there (period,
       inbox and download of the heatmap). The kit has no room for it yet; swap for the kit's when it has one.
     No report has the table view today (no tabular data behind any chart), so `rows` stays empty and the kit hides
     the chart/table toggle by itself. -->
<script setup>
// One chart of a report: its title (with what it measures, on request), the chart, and the same numbers as a table
// for whoever reads better that way or cannot see the marks. A chart with nothing to show says so, in the words of
// the reference. Composed from the registry Card, Tooltip, Table, Empty and ToggleGroup.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChartLine16, DataTable16, Information16 } from '@carbon/icons-vue';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

defineProps({
  title: { type: String, required: true },
  info: { type: String, default: '' }, // what the chart measures, in one sentence
  empty: Boolean, // nothing to show in this period
  emptyTitle: { type: String, default: '' },
  emptyDescription: { type: String, default: '' },
  columns: { type: Array, default: () => [] }, // the table view: heads ...
  rows: { type: Array, default: () => [] }, // ... and rows of cells
});
const { t } = useI18n();
const view = ref('chart');
</script>

<template>
  <Card class="min-w-0 gap-4" data-chart-card>
    <CardHeader class="flex flex-row flex-wrap items-center gap-2">
      <CardTitle class="min-w-0 flex-1 type-polaris-body">
        {{ title }}
      </CardTitle>
      <Tooltip v-if="info">
        <TooltipTrigger as-child>
          <button
            type="button"
            class="flex size-6 items-center justify-center rounded-polaris-md text-polaris-muted-foreground outline-none hover:text-polaris-foreground focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50 p-0"
            :aria-label="t('POLARIS.STAT_TILE.ABOUT', { label: title })"
          >
            <Information16 aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent class="max-w-64">{{ info }}</TooltipContent>
      </Tooltip>
      <!-- PROVISÓRIO (R2-X-4 = B): the woot's header controls; the kit has no room for them yet. -->
      <slot name="actions" />
      <ToggleGroup
        v-if="rows.length && !empty"
        v-model="view"
        type="single"
        variant="outline"
        size="sm"
        :aria-label="t('POLARIS_REPORTS.CHART_CARD.VIEW_OF', { title })"
        @update:model-value="
          value => {
            if (!value) view = 'chart';
          }
        "
      >
        <ToggleGroupItem
          value="chart"
          :aria-label="t('POLARIS_REPORTS.CHART_CARD.CHART')"
        >
          <ChartLine16 aria-hidden="true" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="table"
          :aria-label="t('POLARIS_REPORTS.CHART_CARD.TABLE')"
        >
          <DataTable16 aria-hidden="true" />
        </ToggleGroupItem>
      </ToggleGroup>
    </CardHeader>
    <CardContent>
      <Empty v-if="empty" class="border-0 md:p-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ChartLine16 aria-hidden="true" />
          </EmptyMedia>
          <EmptyTitle>
            {{ emptyTitle || t('POLARIS_REPORTS.CHART_CARD.EMPTY_TITLE') }}
          </EmptyTitle>
          <EmptyDescription>
            {{
              emptyDescription ||
              t('POLARIS_REPORTS.CHART_CARD.EMPTY_DESCRIPTION')
            }}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
      <div v-else-if="view === 'table'" class="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead v-for="column in columns" :key="column">
                {{ column }}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="(row, index) in rows" :key="index">
              <TableCell
                v-for="(cell, c) in row"
                :key="c"
                :class="
                  c === 0 ? 'font-medium' : 'text-polaris-muted-foreground'
                "
              >
                {{ cell }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <slot v-else />
    </CardContent>
  </Card>
</template>
